import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import {
	FileUpload,
	type FileUploadHandlerEvent,
	type FileUploadHeaderTemplateOptions,
} from "primereact/fileupload";
import { type ReactNode, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import { getAcceptAttribute, getDisallowedFiles } from "./file-type-validation";
import { EmptyImageContainer, ShareableButton } from "./files-table.styles";
import { type SelectionSettings } from "./files-table.types";
import { DateTimeDisplay } from "../../../components/DateTimeDisplay";
import { apiBase } from "../../../environment";
import { ERROR_MESSAGES } from "../../../translations/error-messages";
import { FileFilters } from "../file-filters/file-filters.component";

export type FileNode = {
	readonly id: string;
	readonly name: string;
	readonly fileType: string;
	readonly accessType: string;
	readonly uploadDateTime: string;
	readonly thumbnail?: string | null;
	readonly url?: string | null;
};

export type FilesTableUIProps = {
	files: ReadonlyArray<{ readonly node: FileNode | null } | null> | null | undefined;
	hasPrevious: boolean;
	hasNext: boolean;
	loadPrevious: (count: number) => void;
	loadNext: (count: number) => void;
	onRefetch: () => void;
	selectionSettings?: SelectionSettings;
	canUploadFiles: boolean;
	canDeleteFiles: boolean;
	uploadEndpoint: string;
	allowedUploadExtensions?: ReadonlyArray<string>;
	renderDeleteDialog: (fileId: string, onClose: () => void) => ReactNode;
};

export const FilesTableUI = ({
	files,
	hasPrevious,
	hasNext,
	loadPrevious,
	loadNext,
	onRefetch,
	selectionSettings,
	canUploadFiles,
	canDeleteFiles,
	uploadEndpoint,
	allowedUploadExtensions,
	renderDeleteDialog,
}: FilesTableUIProps) => {
	const { t } = useTranslation("common");
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
	const [shareable, setShareable] = useState(false);

	const handleUpload = async (event: FileUploadHandlerEvent) => {
		const { files: uploadFiles, options } = event;

		if (allowedUploadExtensions) {
			const disallowedFiles = getDisallowedFiles(uploadFiles, allowedUploadExtensions);
			if (disallowedFiles.length > 0) {
				toast.error(
					t("files.unsupportedFileType", {
						files: disallowedFiles.map((file) => file.name).join(", "),
						allowed: allowedUploadExtensions.join(", "),
					}),
				);
				options.clear();
				return;
			}
		}

		try {
			const formData = new FormData();
			for (const file of uploadFiles) {
				formData.append("file[]", file);
			}
			formData.append("isPublic", JSON.stringify(shareable));

			const response = await fetch(`${apiBase}${uploadEndpoint}`, {
				method: "POST",
				body: formData,
			});

			if (!response.ok) {
				const errorText = await response.text();
				toast.error(ERROR_MESSAGES[errorText] ?? errorText);
				return;
			}

			options.clear();
			onRefetch();
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Upload failed");
		}
	};

	const customFileUploadHeader = (options: FileUploadHeaderTemplateOptions) => {
		const { className, chooseButton, uploadButton, cancelButton } = options;
		return (
			<div
				className={className}
				style={{ backgroundColor: "transparent", display: "flex", alignItems: "center" }}
			>
				{chooseButton}
				<ShareableButton
					onClick={() => setShareable(!shareable)}
					sharable={shareable}
					label={t("files.shareable")}
					icon="pi pi-share-alt"
				/>
				{uploadButton}
				{cancelButton}
			</div>
		);
	};

	return (
		<>
			{canUploadFiles && (
				<FileUpload
					className="mb-3"
					name="file[]"
					accept={
						allowedUploadExtensions
							? getAcceptAttribute(allowedUploadExtensions)
							: undefined
					}
					customUpload
					multiple
					uploadHandler={handleUpload}
					headerTemplate={customFileUploadHeader}
				/>
			)}
			<FileFilters fileTypeOptions={selectionSettings?.fileTypeOptions} />
			{selectedFileId && renderDeleteDialog(selectedFileId, () => setSelectedFileId(null))}
			<DataTable
				emptyMessage={t("files.noMatchingFiles")}
				className="mb-3"
				value={files?.flatMap((b) => (b?.node ? [b.node] : []))}
				onSelectionChange={selectionSettings?.onSelectionChange}
				selection={selectionSettings?.selection}
				selectionMode={selectionSettings ? "single" : undefined}
			>
				<Column selectionMode="single" style={{ width: "3em" }} />
				<Column
					header={t("files.file")}
					body={(row) => (
						<a href={row.url} rel="noreferrer nofollow" target="_blank">
							{row.thumbnail ? (
								<img height={75} src={row.thumbnail} alt={row.name} />
							) : (
								<EmptyImageContainer className="flex justify-content-center align-items-center">
									{t("files.noPreview")}
								</EmptyImageContainer>
							)}
						</a>
					)}
				/>
				<Column header={t("table.name")} field="name" />
				<Column
					header={t("files.access")}
					body={(row) => (
						<p>
							{row.accessType === "signedUrl"
								? t("files.private")
								: t("files.public")}
						</p>
					)}
				/>
				<Column header={t("files.type")} field="fileType" />
				<Column
					header={t("discountCode.createdAt")}
					body={(row) => <DateTimeDisplay value={row.uploadDateTime} />}
				/>
				{canDeleteFiles && (
					<Column
						header={t("table.actions")}
						style={{ width: "20%" }}
						body={(item) => (
							<Button
								type="button"
								onClick={() => setSelectedFileId(item.id)}
								icon="pi pi-trash"
							/>
						)}
					/>
				)}
			</DataTable>
			<div className="flex justify-content-center align-items-center">
				<Button
					type="button"
					disabled={!hasPrevious}
					onClick={() => loadPrevious(20)}
					className="mr-3 p-button-secondary"
				>
					{t("actions.back")}
				</Button>
				<Button
					type="button"
					className="p-button-secondary"
					disabled={!hasNext}
					onClick={() => loadNext(20)}
				>
					{t("actions.next")}
				</Button>
			</div>
		</>
	);
};
